// src/utils/clientOcrParser.js

/**
 * ============================================================
 * Docker OCR API Client
 * ============================================================
 *
 * React frontend -> FastAPI Docker OCR -> Surya OCR + GPU
 *
 * Docker OCR:
 * http://localhost:8091
 *
 * Expected endpoints:
 * POST /v1/extract
 *
 * document_type:
 *   - aadhaar
 *   - driving_license
 *
 * Expected response:
 * {
 *   data: {
 *     ...
 *   },
 *   confidence: {
 *     ...
 *   },
 *   rawText: "..."
 * }
 */

// ------------------------------------------------------------
// OCR API URL
// ------------------------------------------------------------

const OCR_API_URL =
  process.env.REACT_APP_OCR_API_URL || "https://ocr.eduforestin.com";


// ------------------------------------------------------------
// Aadhaar Verhoeff validation
// ------------------------------------------------------------

const verhoeffTableD = [
  [0, 1, 2, 3, 4, 5, 6, 7, 8, 9],
  [1, 2, 3, 4, 0, 6, 7, 8, 9, 5],
  [2, 3, 4, 0, 1, 7, 8, 9, 5, 6],
  [3, 4, 0, 1, 2, 8, 9, 5, 6, 7],
  [4, 0, 1, 2, 3, 9, 5, 6, 7, 8],
  [5, 9, 8, 7, 6, 0, 4, 3, 2, 1],
  [6, 5, 9, 8, 7, 1, 0, 4, 3, 2],
  [7, 6, 5, 9, 8, 2, 1, 0, 4, 3],
  [8, 7, 6, 5, 9, 3, 2, 1, 0, 4],
  [9, 8, 7, 6, 5, 4, 3, 2, 1, 0],
];

const verhoeffTableP = [
  [0, 1, 2, 3, 4, 5, 6, 7, 8, 9],
  [1, 5, 7, 6, 2, 8, 3, 0, 9, 4],
  [5, 8, 0, 3, 7, 9, 6, 1, 4, 2],
  [8, 9, 1, 6, 0, 4, 3, 5, 2, 7],
  [9, 4, 5, 3, 1, 2, 6, 8, 7, 0],
  [4, 2, 8, 6, 5, 7, 3, 9, 0, 1],
  [2, 7, 9, 3, 8, 0, 6, 4, 1, 5],
  [7, 0, 4, 6, 9, 1, 3, 2, 5, 8],
];

export function validateVerhoeff(value) {
  if (!value) {
    return false;
  }

  const input = String(value)
    .replace(/\s+/g, "")
    .trim();

  if (!/^\d+$/.test(input)) {
    return false;
  }

  let c = 0;

  const invertedArray = input
    .split("")
    .map(Number)
    .reverse();

  for (let i = 0; i < invertedArray.length; i++) {
    c =
      verhoeffTableD[c][
        verhoeffTableP[i % 8][invertedArray[i]]
      ];
  }

  return c === 0;
}


// ------------------------------------------------------------
// Common helpers
// ------------------------------------------------------------

function normalizeError(error) {
  if (!error) {
    return "OCR request failed";
  }

  if (typeof error === "string") {
    return error;
  }

  if (error.message) {
    return error.message;
  }

  return "OCR request failed";
}


function normalizeConfidence(confidence = {}) {
  const result = {};

  Object.keys(confidence || {}).forEach((key) => {
    const item = confidence[key];

    if (!item || typeof item !== "object") {
      result[key] = {
        score: 0,
        status: "needs_verification",
        reason: "Confidence information unavailable",
      };

      return;
    }

    result[key] = {
      score:
        typeof item.score === "number"
          ? item.score
          : 0,

      status:
        item.status ||
        "needs_verification",

      reason:
        item.reason ||
        "",
    };
  });

  return result;
}


function normalizeResponse(result, documentType) {
  if (!result || typeof result !== "object") {
    throw new Error(
      "OCR server returned an invalid response"
    );
  }

  return {
    success:
      result.success !== undefined
        ? result.success
        : true,

    documentType:
      result.documentType ||
      result.document_type ||
      documentType,

    data:
      result.data &&
      typeof result.data === "object"
        ? result.data
        : (result.fields ? { fields: result.fields, lines: result.lines || [] } : {}),

    fields: result.fields || result.data?.fields || {},
    lines: result.lines || result.data?.lines || [],

    confidence:
      normalizeConfidence(result.confidence || result.field_confidence),

    rawText:
      result.rawText ||
      result.raw_text ||
      "",

    message:
      result.message ||
      "",
  };
}


// ------------------------------------------------------------
// Check OCR server
// ------------------------------------------------------------

export async function checkOcrServer() {
  try {
    const response = await fetch(
      `${OCR_API_URL}/health`,
      {
        method: "GET",
      }
    );

    if (!response.ok) {
      return false;
    }

    const result = await response.json();

    return result?.status === "ok";
  } catch (error) {
    console.error(
      "OCR health check failed:",
      error
    );

    return false;
  }
}


// ------------------------------------------------------------
// Main Docker OCR request
// ------------------------------------------------------------

async function extractFromDockerOCR(
  imageFile,
  documentType
) {
  if (!imageFile) {
    throw new Error(
      "Document image is required"
    );
  }

  if (!(imageFile instanceof File) &&
      !(imageFile instanceof Blob)) {
    throw new Error(
      "Invalid image file"
    );
  }

  if (
    documentType !== "aadhaar" &&
    documentType !== "driving_license"
  ) {
    throw new Error(
      "Invalid document type"
    );
  }

  const formData = new FormData();

  formData.append(
    "file",
    imageFile,
    imageFile.name ||
      (
        documentType === "aadhaar"
          ? "aadhaar.jpg"
          : "driving-license.jpg"
      )
  );

  formData.append(
    "document_type",
    documentType
  );

  console.log(
    "================================================"
  );

  console.log(
    "Sending document to Docker OCR"
  );

  console.log(
    "OCR URL:",
    `${OCR_API_URL}/v1/extract`
  );

  console.log(
    "Document type:",
    documentType
  );

  console.log(
    "File:",
    imageFile.name || "blob"
  );

  console.log(
    "File size:",
    imageFile.size
  );

  console.log(
    "File type:",
    imageFile.type
  );

  console.log(
    "================================================"
  );

  let response;

try {
  response = await fetch(
    `${OCR_API_URL}/v1/extract`,
    {
      method: "POST",
      headers: {
        "x-internal-key": "dev-ocr-internal-key",
      },
      body: formData,
    }
  );
} catch (networkError) {
  console.error(
    "Docker OCR network error:",
    networkError
  );

  throw new Error(
    `Cannot connect to OCR server at ${OCR_API_URL}. Make sure Docker OCR is running.`
  );
}

   

  let result = null;

  const responseText =
    await response.text();

  try {
    result =
      responseText
        ? JSON.parse(responseText)
        : null;
  } catch (jsonError) {
    console.error(
      "OCR server returned non-JSON response:",
      responseText
    );

    throw new Error(
      `OCR server returned invalid JSON (${response.status})`
    );
  }

  console.log(
    "OCR HTTP status:",
    response.status
  );

  console.log(
    "OCR response:",
    result
  );

  if (!response.ok) {
    let message =
      result?.detail ||
      result?.message ||
      `OCR request failed with status ${response.status}`;

    if (Array.isArray(result?.detail)) {
      message = result.detail
        .map((item) => {
          if (typeof item === "string") {
            return item;
          }

          return (
            item?.msg ||
            "Validation error"
          );
        })
        .join(", ");
    }

    throw new Error(message);
  }

  const normalized =
    normalizeResponse(
      result,
      documentType
    );

  console.log(
    "Normalized OCR response:",
    normalized
  );

  return normalized;
}


// ------------------------------------------------------------
// Aadhaar OCR
// ------------------------------------------------------------

export async function extractAadhaarFromImage(
  imageFile
) {
  return extractFromDockerOCR(
    imageFile,
    "aadhaar"
  );
}


// ------------------------------------------------------------
// Driving Licence OCR
// ------------------------------------------------------------

export async function extractDlFromImage(
  imageFile
) {
  return extractFromDockerOCR(
    imageFile,
    "driving_license"
  );
}


// ------------------------------------------------------------
// Default export
// ------------------------------------------------------------

const clientOcrParser = {
  extractAadhaarFromImage,
  extractDlFromImage,
  validateVerhoeff,
  checkOcrServer,
};

export default clientOcrParser;